import { useCookie, useRuntimeConfig } from '#imports'
import { computed, reactive, ref, watch } from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'

import { DIDOMI_COOKIE_NAME, VENDORS_ID } from '../didomi/config'
import { isClient } from '../utils/isClient'

export function useDataConsent() {
  const { market } = useMarketplace()
  const {
    tracking: { didomi, countriesWithNoCookieOptIn },
  } = useRuntimeConfig().public
  const cookie = useCookie<string | null>(DIDOMI_COOKIE_NAME)
  const logger = useLogger()

  const isDidomiEnabled =
    didomi.isEnabled && didomi.enabledCountryCodes.includes(market.countryCode)

  const vendorAcceptanceStates = reactive<
    Record<keyof typeof VENDORS_ID, boolean>
  >({
    amplitude: false,
    braze: false,
    gtm: false,
    appsFlyer: false,
  })

  let hasLoggedMissingVendors = false

  // Determine if modal should be displayed based on the country and the existence of the cookie consent cookie
  const shouldDisplayModal = (hasConsentCookie: boolean) => {
    return (
      !countriesWithNoCookieOptIn.includes(market.countryCode) &&
      !hasConsentCookie
    )
  }

  const isCookiesModalDisplayed = ref(shouldDisplayModal(!!cookie.value))

  watch(
    cookie,
    () => {
      isCookiesModalDisplayed.value = shouldDisplayModal(!!cookie.value)
    },
    { immediate: false },
  )

  function showCookiesModal() {
    if (!isClient()) return

    window.Didomi?.preferences?.show?.()
  }

  function acceptAll() {
    if (!isClient()) return

    window.Didomi?.setUserAgreeToAll?.()
  }

  function declineAll() {
    if (!isClient()) return

    window.Didomi?.setUserDisagreeToAll?.()
  }

  function getUserConsentForVendor(
    vendor: (typeof VENDORS_ID)[keyof typeof VENDORS_ID],
  ): boolean {
    try {
      if (!isDidomiEnabled || !isClient() || !window.Didomi) return false

      if (
        !window.Didomi.getCurrentUserStatus ||
        typeof window.Didomi.getCurrentUserStatus !== 'function'
      ) {
        return false
      }

      if (
        !window.Didomi.getCurrentUserStatus().vendors ||
        !window.Didomi.getCurrentUserStatus().vendors?.[vendor]
      ) {
        if (!hasLoggedMissingVendors) {
          logger.error('[Didomi] Error getting vendors', {
            vendors: window.Didomi.getCurrentUserStatus()?.vendors,
            owners: ['bot-group-onboard-front'],
          })

          hasLoggedMissingVendors = true
        }

        return false
      }

      return window.Didomi.getCurrentUserStatus().vendors[vendor].enabled
    } catch (error) {
      logger.error(`[Didomi] Error getting user consent for vendor ${vendor}`, {
        error: error as Error,
        owners: ['bot-group-onboard-front'],
      })

      return false
    }
  }

  function getUserConsentForAmplitude() {
    return getUserConsentForVendor(VENDORS_ID.amplitude)
  }

  function getUserConsentForBraze() {
    return getUserConsentForVendor(VENDORS_ID.braze)
  }

  function getNoticeVisible() {
    if (!isDidomiEnabled || !isClient() || !window.Didomi) return false

    return window.Didomi.notice?.isVisible() ?? false
  }

  function updateVendorAcceptanceStates() {
    for (const [vendorKey, vendorId] of Object.entries(VENDORS_ID)) {
      const vendor = vendorKey as keyof typeof VENDORS_ID
      vendorAcceptanceStates[vendor] = getUserConsentForVendor(vendorId)
    }
  }

  const isAppsFlyerUserConsentGiven = computed(() => {
    if (!window?.Didomi) return false

    return vendorAcceptanceStates.appsFlyer
  })

  const isBrazeUserConsentGiven = computed(() => {
    if (!window?.Didomi) return false

    return vendorAcceptanceStates.braze
  })

  if (isDidomiEnabled && isClient() && window?.Didomi) {
    setTimeout(() => {
      updateVendorAcceptanceStates()
    }, 0)

    // consent can be changed by the user at any time during app navigation
    window.Didomi.on?.('consent.changed', () => {
      updateVendorAcceptanceStates()
    })
  }

  return {
    acceptAll,
    declineAll,
    showCookiesModal,
    getUserConsentForAmplitude,
    getUserConsentForBraze,
    isAppsFlyerUserConsentGiven,
    isBrazeUserConsentGiven,
    isDidomiEnabled,
    isCookiesModalDisplayed,
    getNoticeVisible,
  }
}
